import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, FileText } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, SectionLabel, ErrorState } from '@/acc-ui/primitives/Layout';
import { MarkdownViewer } from '@/acc-ui/primitives/MarkdownViewer';
import { Button } from '@/acc-ui/primitives/Button';
import { Badge } from '@/acc-ui/primitives/Badge';
import { toast } from '@/acc-ui/primitives/Toast';
import { settingsApi } from '@/api/other-apis';
import { cn } from '@/lib/utils';

/**
 * Settings → Documentation + System Info.
 *
 * Left column: list of modules (each has a FUNCTIONALITY.md). Right
 * column: rendered markdown for the selected module. Top: system info
 * card with version, LLM provider, region, uptime, feature flags.
 *
 * The "Resync" button re-reads FUNCTIONALITY.md files from disk into
 * settings.doc_index. Useful if ops edits a doc temporarily without a
 * redeploy — the next startup also syncs.
 */

export function SettingsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const docsQuery = useQuery({ queryKey: ['docs'], queryFn: settingsApi.listDocs });
  const sysQuery = useQuery({ queryKey: ['systemInfo'], queryFn: settingsApi.systemInfo });

  // Default to first module on load
  useEffect(() => {
    if (!selected && docsQuery.data && docsQuery.data.length > 0) {
      setSelected(docsQuery.data[0]!.module);
    }
  }, [selected, docsQuery.data]);

  const resync = useMutation({
    mutationFn: settingsApi.resyncDocs,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['docs'] });
      toast.success('Documentation resynced from disk.');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Resync failed.'),
  });

  const selectedDoc = docsQuery.data?.find((d) => d.module === selected) ?? null;

  return (
    <AppShell crumb="Admin" title="Settings">
      <PageHeader
        title="Settings"
        subtitle="Platform documentation and system info."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw className={cn('w-3.5 h-3.5', resync.isPending && 'animate-spin')} />}
            onClick={() => resync.mutate()}
            loading={resync.isPending}
          >
            Resync docs
          </Button>
        }
      />

      {/* System info strip */}
      <Card padding={false}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-0 divide-x divide-glass-hairline">
          <InfoCell label="Product" value={sysQuery.data?.productName ?? '—'} />
          <InfoCell
            label="Environment"
            value={sysQuery.data?.environment ?? '—'}
            mono
            tone={sysQuery.data?.environment === 'production' ? 'success' : 'warning'}
          />
          <InfoCell label="Commit" value={sysQuery.data?.gitCommit ?? 'local'} mono />
          <InfoCell label="LLM" value={sysQuery.data?.llmModel ?? '—'} mono />
          <InfoCell label="Region" value={sysQuery.data?.region ?? '—'} mono />
          <InfoCell
            label="Uptime"
            value={sysQuery.data ? formatUptime(sysQuery.data.uptimeSeconds) : '—'}
          />
        </div>
        {sysQuery.data?.featureFlags && Object.keys(sysQuery.data.featureFlags).length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-glass-hairline">
            <SectionLabel>Feature flags</SectionLabel>
            {Object.entries(sysQuery.data.featureFlags).map(([k, v]) => (
              <Badge key={k} tone={v ? 'success' : 'muted'} dot>
                {k}
              </Badge>
            ))}
          </div>
        )}
      </Card>

      {/* Docs viewer */}
      {docsQuery.isError ? (
        <ErrorState
          description="Could not load documentation."
          action={<Button onClick={() => docsQuery.refetch()}>Retry</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3">
          <Card padding={false} className="md:sticky md:top-[5.5rem] md:self-start">
            <div className="px-3 py-2 border-b border-glass-hairline">
              <SectionLabel>Modules</SectionLabel>
            </div>
            <nav className="flex flex-col">
              {(docsQuery.data ?? []).map((d) => (
                <button
                  key={d.module}
                  onClick={() => setSelected(d.module)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-left text-label transition-colors border-b border-glass-hairline last:border-b-0',
                    d.module === selected
                      ? 'bg-brand-blue/10 text-brand-blue font-semibold'
                      : 'hover:bg-slate-900/5 text-text-secondary',
                  )}
                >
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{d.title}</span>
                </button>
              ))}
              {docsQuery.data?.length === 0 && (
                <div className="px-3 py-4 text-caption text-text-tertiary">
                  No module docs yet. Run resync.
                </div>
              )}
            </nav>
          </Card>

          <Card>
            {!selectedDoc && (
              <div className="text-label text-text-tertiary py-8 text-center">
                Select a module from the left to read its documentation.
              </div>
            )}
            {selectedDoc && (
              <>
                <div className="flex items-center justify-between gap-3 pb-3 mb-3 border-b border-glass-hairline">
                  <div>
                    <h2 className="text-title font-semibold text-text-primary">
                      {selectedDoc.title}
                    </h2>
                    <div className="text-micro text-text-tertiary mt-0.5">
                      Updated {new Date(selectedDoc.updatedAt).toLocaleString()}
                      {selectedDoc.gitCommit && (
                        <>
                          {' · '}
                          <code className="font-mono">{selectedDoc.gitCommit}</code>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <MarkdownViewer source={selectedDoc.contentMd} />
              </>
            )}
          </Card>
        </div>
      )}
    </AppShell>
  );
}

function InfoCell({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'success' | 'warning' | 'neutral';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-emerald-700'
      : tone === 'warning'
      ? 'text-amber-700'
      : 'text-text-primary';
  return (
    <div className="px-4 py-3 min-w-0">
      <SectionLabel>{label}</SectionLabel>
      <div
        className={cn(
          'mt-1 text-label truncate',
          mono && 'font-mono text-caption',
          toneClass,
        )}
      >
        {value}
      </div>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
